import {
  oklchColor,
  oklchToRgb,
  rgbToOklch,
  type OklchColor,
  type RgbColor,
} from "./color";
import { CSS_NAMED_COLORS } from "./cssNamedColors";

const D65 = {
  x: 0.95047,
  y: 1,
  z: 1.08883,
};

export function formatHexLabel(color: OklchColor): string {
  const rgb = oklchToRgb(color);
  const hex = `${toHexByte(rgb.r)}${toHexByte(rgb.g)}${toHexByte(rgb.b)}`;
  if (rgb.alpha < 0.999) {
    return `${hex}${toHexByte(rgb.alpha * 255)}`.toUpperCase();
  }
  return hex.toUpperCase();
}

export function formatHexRgbLabel(color: OklchColor): string {
  const rgb = oklchToRgb(color);
  return `${toHexByte(rgb.r)}${toHexByte(rgb.g)}${toHexByte(rgb.b)}`.toUpperCase();
}

export function tryParseColorString(input: string): OklchColor | undefined {
  const trimmed = input.trim();
  if (!trimmed) {
    return undefined;
  }

  return parseHexColor(trimmed)
    ?? parseNamedColor(trimmed)
    ?? parseRgbColor(trimmed)
    ?? parseOklchColor(trimmed)
    ?? parseLabColor(trimmed)
    ?? parseLchColor(trimmed);
}

function parseNamedColor(input: string): OklchColor | undefined {
  const hex = CSS_NAMED_COLORS[input.toLowerCase()];
  return hex ? parseHexColor(hex) : undefined;
}

function parseHexColor(input: string): OklchColor | undefined {
  const hex = input.replace(/^#/, "");
  if (!/^[0-9a-fA-F]{3,8}$/.test(hex)) {
    return undefined;
  }

  const expanded = hex.length === 3 || hex.length === 4
    ? hex.split("").map(character => `${character}${character}`).join("")
    : hex.length === 6
      ? hex
      : hex.slice(0, 8);

  const value = Number.parseInt(expanded.slice(0, 6), 16);
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const alpha = expanded.length >= 8
    ? Number.parseInt(expanded.slice(6, 8), 16) / 255
    : 1;

  return rgbToOklch({
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
    alpha: clamp01(alpha),
  });
}

function parseRgbColor(input: string): OklchColor | undefined {
  const match = input.match(/^rgba?\((.+)\)$/i);
  if (!match) {
    return undefined;
  }

  const [componentsPart, alphaPart] = splitAlpha(match[1]!);
  const components = tokenizeComponents(componentsPart);
  if (components.length < 3) {
    return undefined;
  }

  const channels = components.slice(0, 3).map(parseRgbChannel);
  if (channels.some(channel => channel === undefined)) {
    return undefined;
  }

  const alpha = alphaPart !== undefined
    ? parseAlpha(alphaPart)
    : components[3] !== undefined
      ? parseAlpha(components[3]!)
      : 1;
  if (alpha === undefined) {
    return undefined;
  }

  const [r, g, b] = channels as [number, number, number];
  return rgbToOklch({ r, g, b, alpha });
}

function parseOklchColor(input: string): OklchColor | undefined {
  const match = input.match(/^oklch\((.+)\)$/i);
  if (!match) {
    return undefined;
  }

  const [componentsPart, alphaPart] = splitAlpha(match[1]!);
  const components = tokenizeComponents(componentsPart);
  if (components.length < 3) {
    return undefined;
  }

  const lightness = parseLightness(components[0]!);
  const chroma = parseNumber(components[1]!);
  const hue = parseHue(components[2]!);
  const alpha = alphaPart !== undefined
    ? parseAlpha(alphaPart)
    : components[3] !== undefined
      ? parseAlpha(components[3]!)
      : 1;
  if (lightness === undefined || chroma === undefined || hue === undefined || alpha === undefined) {
    return undefined;
  }

  return oklchColor(lightness, chroma, hue, alpha);
}

function parseLabColor(input: string): OklchColor | undefined {
  const match = input.match(/^lab\((.+)\)$/i);
  if (!match) {
    return undefined;
  }

  const [componentsPart, alphaPart] = splitAlpha(match[1]!);
  const components = tokenizeComponents(componentsPart);
  if (components.length < 3) {
    return undefined;
  }

  const lightness = parseLabLightness(components[0]!);
  const a = parseSignedNumber(components[1]!);
  const b = parseSignedNumber(components[2]!);
  const alpha = alphaPart !== undefined
    ? parseAlpha(alphaPart)
    : components[3] !== undefined
      ? parseAlpha(components[3]!)
      : 1;
  if (lightness === undefined || a === undefined || b === undefined || alpha === undefined) {
    return undefined;
  }

  return rgbToOklch(labToRgb(lightness, a, b, alpha));
}

function parseLchColor(input: string): OklchColor | undefined {
  const match = input.match(/^lch\((.+)\)$/i);
  if (!match) {
    return undefined;
  }

  const [componentsPart, alphaPart] = splitAlpha(match[1]!);
  const components = tokenizeComponents(componentsPart);
  if (components.length < 3) {
    return undefined;
  }

  const lightness = parseLabLightness(components[0]!);
  const chroma = parseNumber(components[1]!);
  const hue = parseHue(components[2]!);
  const alpha = alphaPart !== undefined
    ? parseAlpha(alphaPart)
    : components[3] !== undefined
      ? parseAlpha(components[3]!)
      : 1;
  if (lightness === undefined || chroma === undefined || hue === undefined || alpha === undefined) {
    return undefined;
  }

  const radians = hue * Math.PI / 180;
  return rgbToOklch(labToRgb(lightness, chroma * Math.cos(radians), chroma * Math.sin(radians), alpha));
}

function splitAlpha(value: string): [string, string | undefined] {
  const slashIndex = value.indexOf("/");
  if (slashIndex < 0) {
    return [value, undefined];
  }

  return [value.slice(0, slashIndex).trim(), value.slice(slashIndex + 1).trim()];
}

function tokenizeComponents(value: string): string[] {
  return value
    .split(/[\s,/]+/)
    .map(token => token.trim())
    .filter(Boolean);
}

function parseRgbChannel(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    const percent = Number.parseFloat(trimmed.slice(0, -1));
    return Number.isFinite(percent) ? clamp(Math.round(percent / 100 * 255), 0, 255) : undefined;
  }

  const number = Number.parseFloat(trimmed);
  return Number.isFinite(number) ? clamp(Math.round(number), 0, 255) : undefined;
}

function parseLightness(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    const percent = Number.parseFloat(trimmed.slice(0, -1));
    return Number.isFinite(percent) ? clamp01(percent / 100) : undefined;
  }

  const number = Number.parseFloat(trimmed);
  if (!Number.isFinite(number)) {
    return undefined;
  }

  return number > 1 ? clamp01(number / 100) : clamp01(number);
}

function parseLabLightness(value: string): number | undefined {
  const trimmed = value.trim();
  const number = trimmed.endsWith("%")
    ? Number.parseFloat(trimmed.slice(0, -1))
    : Number.parseFloat(trimmed);
  return Number.isFinite(number) ? number : undefined;
}

function parseNumber(value: string): number | undefined {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : undefined;
}

function parseSignedNumber(value: string): number | undefined {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : undefined;
}

function parseHue(value: string): number | undefined {
  const trimmed = value.replace(/deg$/i, "").trim();
  const number = Number.parseFloat(trimmed);
  return Number.isFinite(number) ? number : undefined;
}

function parseAlpha(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    const percent = Number.parseFloat(trimmed.slice(0, -1));
    return Number.isFinite(percent) ? clamp01(percent / 100) : undefined;
  }

  const number = Number.parseFloat(trimmed);
  return Number.isFinite(number) ? clamp01(number) : undefined;
}

function labToRgb(lightness: number, a: number, b: number, alpha: number): RgbColor {
  const fy = (lightness + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;
  const x = D65.x * labPivot(fx);
  const y = D65.y * labPivot(fy);
  const z = D65.z * labPivot(fz);

  const linearR = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z;
  const linearG = -0.969266 * x + 1.8760108 * y + 0.041556 * z;
  const linearB = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z;

  return {
    r: Math.round(clamp(linearToSrgb(linearR), 0, 1) * 255),
    g: Math.round(clamp(linearToSrgb(linearG), 0, 1) * 255),
    b: Math.round(clamp(linearToSrgb(linearB), 0, 1) * 255),
    alpha,
  };
}

function labPivot(value: number): number {
  const cubed = value ** 3;
  return cubed > 0.008856 ? cubed : (value - 16 / 116) / 7.787;
}

function linearToSrgb(value: number): number {
  return value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function toHexByte(value: number): string {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
}

// Keep hexToRgb in sync for callers that pass 8-digit hex with hash.
export function parseHexColorString(input: string): OklchColor | undefined {
  return parseHexColor(input);
}
